import { useTonAddress } from "@tonconnect/ui-react";
import QuestiomMarkImg from "assets/icons/question.png";
import { useJettonAddress } from "hooks/useJettonAddress";
import useNotification from "hooks/useNotification";
import { jettonDeployController } from "lib/deploy-controller";
import { zeroAddress } from "lib/utils";
import { useCallback } from "react";
import { useRecoilState, useResetRecoilState } from "recoil";
import { Address } from "ton";
import { isValidAddress } from "utils";
import { jettonStateAtom } from ".";
import { useSearchParams } from "react-router-dom";
import { getTonRpcErrorMessage } from "lib/ton-rpc-client";

let i = 0;

function useJettonStore() {
  const [state, setState] = useRecoilState(jettonStateAtom);
  const reset = useResetRecoilState(jettonStateAtom);
  const { showNotification } = useNotification();
  const connectedWalletAddress = useTonAddress();
  const { jettonAddress } = useJettonAddress();
  const [params] = useSearchParams();
  const queryWalletAddress = params.get("address");

  const getJettonDetails = useCallback(async () => {
    i++;
    const myIndex = i;

    let queryAddress = queryWalletAddress;

    if (queryAddress && !isValidAddress(queryAddress)) {
      queryAddress = null;
      showNotification("Invalid jetton address in query param", "error", undefined, 5000);
    }

    let address: Address | null = null;
    try {
      address = Address.parse(queryAddress || connectedWalletAddress);
    } catch (error) {}

    let userAddress: Address | null = null;
    try {
      userAddress = Address.parse(connectedWalletAddress);
    } catch (error) {}

    const isMyWallet = address && userAddress ? address.equals(userAddress) : false;

    reset();

    if (!jettonAddress || !isValidAddress(jettonAddress)) {
      showNotification("Invalid jetton address", "error");
      return;
    }

    const parsedJettonMaster = Address.parse(jettonAddress);

    try {
      setState((prevState) => ({
        ...prevState,
        jettonLoading: true,
        jettonError: undefined,
      }));

      const result = await jettonDeployController.getJettonDetails(parsedJettonMaster, address);

      if (!result) {
        console.log("empty");

        return;
      }
      const _adminAddress = result.minter.admin?.toFriendly() ?? zeroAddress().toFriendly();
      const adminAddress = Address.parse(_adminAddress);
      const admin = (isMyWallet && userAddress && adminAddress.equals(userAddress)) || false;

      let image: string | undefined;

      if (result.minter.metadata.image) {
        const img = new Image();
        img.src = result.minter.metadata.image;
        img.onerror = () => {
          if (myIndex !== i) return;
          setState((prev) => ({ ...prev, isImageBroken: true }));
        };

        image = result.minter.metadata.image;
      } else if (result.minter.metadata.image_data) {
        try {
          const imgData = Buffer.from(result.minter.metadata.image_data, "base64").toString();
          let type: string;

          if (/<svg xmlns/.test(imgData)) {
            type = "svg+xml";
          } else if (/png/i.test(imgData)) {
            type = "png";
          } else {
            console.warn("Defaulting to jpeg");
            type = "jpeg"; // Fallback
          }

          image = `data:image/${type};base64,${result.minter.metadata.image_data}`;
        } catch (e) {
          console.error("Error parsing img metadata");
        }
      }

      if (myIndex !== i) {
        return;
      }
      setState((prevState) => {
        return {
          ...prevState,
          isJettonDeployerFaultyOnChainData: result.minter.isJettonDeployerFaultyOnChainData,
          persistenceType: result.minter.persistenceType,
          description: result.minter.metadata.description,
          jettonImage: image ?? QuestiomMarkImg,
          totalSupply: result.minter.totalSupply,
          name: result.minter.metadata.name,
          symbol: result.minter.metadata.symbol,
          adminRevokedOwnership: zeroAddress().equals(adminAddress),
          isAdmin: admin,
          decimals: result.minter.metadata.decimals || "9",
          adminAddress: adminAddress.toFriendly({ urlSafe: true, bounceable: false }),
          balance: result.jettonWallet ? result.jettonWallet.balance : undefined,
          jettonWalletAddress: result.jettonWallet?.jWalletAddress?.toFriendly(),
          jettonMaster: jettonAddress,
          isMyWallet,
          selectedWalletAddress: address?.toFriendly({ urlSafe: true, bounceable: false }),
        };
      });
    } catch (error) {
      if (myIndex !== i) return;
      if (error instanceof Error) {
        console.error(error);
      }
      const message =
        error instanceof Error && /exit_code: (11|32)/.test(error.message)
          ? "无法读取代币信息，请确认这是 Jetton 合约地址"
          : getTonRpcErrorMessage(error);
      setState((prev) => ({ ...prev, jettonError: message }));
    } finally {
      if (myIndex === i) {
        setState((prevState) => ({
          ...prevState,
          jettonLoading: false,
        }));
      }
    }
  }, [
    setState,
    showNotification,
    connectedWalletAddress,
    jettonAddress,
    queryWalletAddress,
    reset,
  ]);

  return {
    ...state,
    getJettonDetails,
    reset,
  };
}

export default useJettonStore;
